import { Box, Center, Group } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { TeamOut } from '@repo/api-codegen';
import { IconWrapper } from '@repo/common/components/IconWrapper';
import { Button, Icon, MultiSelect, Text } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { IUser, IUserGroup } from '../../api';
import { useMembershipsAndBulkCreateMemberships } from '../../api/hooks/team/teamsManagement';
import { useExtendedUserList } from '../../api/hooks/user/useExtendedUserList';
import { getDisplayName } from '../../utils/userUtils';
import { currentUser } from '../Documentation/Documentation.stories.constants';
import { UserAvatar } from '../UserAvatar';

type UserOrGroupItem = { value: string; group: string } & (
	| {
			group: 'Select a person';
			user: IUser;
	  }
	| {
			group: 'Select a group';
			userGroup: IUserGroup;
	  }
);
const prepareData = (selectedValues: string[]) =>
	selectedValues.map((value) => {
		const [key, id] = value.split(':');
		if (key === 'user_id') {
			return {
				user_id: id,
			};
		}
		return {
			user_group_id: id,
		};
	});

export function InviteTeamMemberForm({
	team,
	onFinish,
	searchValue,
	setSearchValue,
	placeholder,
}: {
	team: TeamOut;
	onFinish?: (teamId: string) => void;
	searchValue?: string;
	setSearchValue?: (value: string) => void;
	placeholder: string;
}) {
	const [value, setValue] = useState<string[]>([]);
	const navigate = useNavigate();

	const { activeUsers: usersData, userGroups: groupsData } =
		useExtendedUserList();

	const {
		memberships: existingMemberships,
		isBulkCreatingMemberships: isLoading,
		bulkCreateTeamMembership,
	} = useMembershipsAndBulkCreateMemberships(team.id);

	const data: UserOrGroupItem[] = useMemo(() => {
		const users =
			usersData?.filter(
				(user) =>
					!existingMemberships?.results?.find(
						(membership) => membership.user?.id === user.id
					) && user.id !== currentUser.id
			) ?? [];

		const userGroups =
			groupsData?.filter(
				(userGroup: { id: string | undefined }) =>
					!existingMemberships?.results.find(
						(membership) => membership.user_group?.id === userGroup.id
					)
			) ?? [];

		return [
			...(users
				.filter((u) => !u.disabled)
				.map((user) => ({
					label: getDisplayName(user),
					value: `user_id:${user.id}`,
					group: 'Select a person' as const,
					user,
				})) as UserOrGroupItem[]),
			...(userGroups.map((userGroup: { name: string; id: string }) => ({
				label: userGroup.name,
				value: `user_group_id:${userGroup.id}`,
				group: 'Select a group' as const,
				userGroup,
			})) as unknown as UserOrGroupItem[]),
		];
	}, [existingMemberships?.results, groupsData, usersData]);

	const filteredValue = value.filter((v) =>
		data.find((item) => item.value === v)
	);

	const invite = useCallback(async () => {
		await bulkCreateTeamMembership({
			pathParams: {
				teamId: team.id,
			},
			body: prepareData(value),
		});

		showNotification({
			title: 'Members added',
			message: `Added members to ${team.name}`,
			color: 'green',
			icon: <Icon name="check" color="text/success-on-fill/default" />,
		});

		onFinish?.(team.id);
	}, [bulkCreateTeamMembership, onFinish, team.id, team.name, value]);

	const handleInvitePeopleClick = useCallback(() => {
		navigate('/settings/members');
	}, [navigate]);

	return (
		<Group noWrap>
			<Box
				sx={{
					flexGrow: 1,
					flexShrink: 1,
				}}
			>
				<MultiSelect
					data={data}
					placeholder={placeholder}
					value={filteredValue}
					searchValue={searchValue}
					onSearchChange={setSearchValue}
					setValue={setValue}
					renderIcon={(item) =>
						item.group === 'Select a person' ? (
							<Center p={2}>
								<UserAvatar user={item.user} size="xxs" />
							</Center>
						) : (
							<IconWrapper>
								<Text size="xs">{item.userGroup.icon}</Text>
							</IconWrapper>
						)
					}
					renderLabel={(item) =>
						item.group === 'Select a person'
							? getDisplayName(item.user)
							: item.userGroup.name
					}
					nothingFound={
						<Button
							w="100%"
							size="sm"
							variant="tertiary"
							leftIconName="send"
							onClick={handleInvitePeopleClick}
						>
							Invite people...
						</Button>
					}
				/>
			</Box>

			<Button
				size="md"
				variant="primary"
				disabled={filteredValue.length === 0}
				loading={isLoading}
				onClick={invite}
			>
				Add
			</Button>
		</Group>
	);
}
